import * as Sentry from "@sentry/react";

import dunegql from "src/utils/dunegqlCodegen/dunegql";
import { useUpsertQueryEmailAlertMutation } from "src/gql/codegen/duneGraphql";

import type { SessionWithUser } from "src/utils/users/types";
import type { ScheduleQueryFormState } from "shared/ScheduleExecutionModalContent";

import { isNonNullable, isNullable } from "src/utils/types/types";
import { getQueryAlertsQueryOptions } from "./useQueryAlert";

type ScheduleQueryFormStateWithEmails = Omit<
  ScheduleQueryFormState,
  "alerts"
> & {
  alerts: {
    emails: string[];
  };
};

export const formStateHasEmailAlert = (
  formState: ScheduleQueryFormState
): formState is ScheduleQueryFormStateWithEmails =>
  isNonNullable(formState.alerts.emails);

export function useUpsertQueryEmailAlert() {
  const [callUpsertQueryEmailAlert, upsertQueryEmailAlertResult] =
    useUpsertQueryEmailAlertMutation();

  function upsertQueryEmailAlert(
    session: SessionWithUser,
    cronJobId: string,
    alertId: string | undefined,
    form: ScheduleQueryFormStateWithEmails
  ): Promise<void> {
    if (isNullable(session) || upsertQueryEmailAlertResult.loading) {
      return new Promise(() => {});
    }

    return new Promise((resolve) => {
      callUpsertQueryEmailAlert({
        context: { session },
        variables: {
          input: {
            cronJobId,
            id: alertId,
            emails: form.alerts.emails,
          },
        },
        refetchQueries: [getQueryAlertsQueryOptions(session, cronJobId)],
      }).then(
        () => {
          resolve();
        },
        (error) => {
          Sentry.captureException(
            "Failed to upsert query email alert: " + error.message
          );
        }
      );
    });
  }

  return [upsertQueryEmailAlert, upsertQueryEmailAlertResult] as const;
}

dunegql`
  mutation UpsertQueryEmailAlert($input: UpsertQueryEmailAlertInput!) {
    upsertQueryEmailAlert(input: $input) {
      id
    }
  }
`;
